import { field } from '../fields'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, CalendarRange, DoorOpen, Plus } from 'lucide-react'
import {
  archiveRoom,
  archiveStudioDate,
  capabilityLabel,
  createRoom,
  createSeason,
  createStudioDate,
  fetchRooms,
  fetchSeasons,
  fetchStudioDates,
  retireBlocker,
  saveRoom,
  saveSeason,
  saveStudioDate,
  seasonDateProblem,
  studioDateProblem,
  type Room,
  type RoomEdit,
  type Season,
  type SeasonEdit,
  type StudioDate,
  type StudioDateEdit,
} from '../api/studioSetup'
import { formatFullDate } from '../api/calendar'
/**
 * Rooms and seasons (D-157).
 *
 * Two small screens rather than one each, because they are opened for the same
 * reason — something in the studio's own setup is wrong — and separately they
 * would each be a nav entry that earns its place about once a season.
 *
 * **Season *planning* is not here.** Program defaults, class dates and the
 * timetable are M8 and a later phase; this is for correcting what exists.
 */
export default function StudioSetupView() {
  return (
    <div className="mx-auto w-full max-w-[46rem] px-5 py-8">
      <header className="mb-6">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Studio</p>
        <h1 className="font-display text-2xl">Studio setup</h1>
        <p className="mt-1.5 max-w-[40rem] text-[13.5px] text-ink-3">
          None of this comes from Jackrabbit &mdash; the room colours the calendar is drawn
          in, the season the dates hang off, and the dates the studio works towards live
          only here.
        </p>
      </header>

      <Rooms />
      <Seasons />
      <KeyDates />
    </div>
  )
}

function Rooms() {
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)

  const { data, isPending } = useQuery({ queryKey: ['admin-rooms'], queryFn: fetchRooms })
  const put = (rooms: Room[]) => queryClient.setQueryData(['admin-rooms'], rooms)

  return (
    <section className="mb-8">
      <header className="mb-3 flex items-center gap-2.5">
        <DoorOpen size={16} strokeWidth={2} className="shrink-0 text-ink-3" />
        <h2 className="flex-1 text-[13px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          Rooms
        </h2>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent-2"
          >
            <Plus size={13} strokeWidth={2.5} />
            Add a room
          </button>
        )}
      </header>

      {isPending && <p className="text-[13px] text-ink-3">Loading...</p>}

      {adding && (
        <RoomForm
          onCancel={() => setAdding(false)}
          onSaved={(rooms) => {
            put(rooms)
            setAdding(false)
          }}
        />
      )}

      <div className="flex flex-col gap-2">
        {data?.map((room) => <RoomRow key={room.roomId} room={room} onSaved={put} />)}
      </div>
    </section>
  )
}

function RoomRow({ room, onSaved }: { room: Room; onSaved: (rooms: Room[]) => void }) {
  const [open, setOpen] = useState(false)

  const blocker = retireBlocker(room)
  const capabilities = capabilityLabel(room)

  const retire = useMutation({
    mutationFn: () => archiveRoom(room.roomId),
    onSuccess: onSaved,
  })

  return (
    <div
      className={`rounded-2xl border bg-surface px-4 py-3 ${
        room.isArchived ? 'border-line opacity-60' : 'border-line'
      }`}
    >
      <div className="flex flex-wrap items-center gap-3">
        {/* The colour is the point — this is what the calendar looks like. */}
        <span
          className="size-4 shrink-0 rounded-full border border-line-2"
          style={{ background: room.color ?? 'transparent' }}
          aria-hidden="true"
        />

        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold wrap-anywhere">
            {room.name}
            {room.isArchived && (
              <span className="ml-2 rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px] font-semibold text-ink-3">
                Retired
              </span>
            )}
          </p>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            {room.capacity !== null && <span className="tabular">Fits {room.capacity}</span>}
            {room.capacity !== null && capabilities && ' · '}
            {capabilities}
            {(room.capacity !== null || capabilities) && room.classes > 0 && ' · '}
            {room.classes > 0 && (
              <span className="tabular">
                {room.classes} {room.classes === 1 ? 'class' : 'classes'}
              </span>
            )}
          </p>
        </div>

        {!open && !room.isArchived && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
          >
            Edit
          </button>
        )}
      </div>

      {open && (
        <RoomForm
          room={room}
          onCancel={() => setOpen(false)}
          onSaved={(rooms) => {
            onSaved(rooms)
            setOpen(false)
          }}
          footer={
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => retire.mutate()}
                disabled={blocker !== null || retire.isPending}
                title={blocker ?? 'Keeps every attendance record and booking that ever happened here'}
                className="rounded-lg border border-line-2 px-3 py-2 text-[12.5px] font-semibold text-crit disabled:cursor-not-allowed disabled:opacity-40"
              >
                Retire this room
              </button>

              {/*
                The reason, not just a dead button. "You cannot" without
                "because eleven classes are in it" leaves somebody clicking.
              */}
              {blocker && <span className="text-[12px] text-ink-3">{blocker}</span>}
              {retire.isError && (
                <span className="text-[12px] text-crit">{(retire.error as Error).message}</span>
              )}
            </div>
          }
        />
      )}
    </div>
  )
}

function RoomForm({
  room,
  onCancel,
  onSaved,
  footer,
}: {
  room?: Room
  onCancel: () => void
  onSaved: (rooms: Room[]) => void
  footer?: React.ReactNode
}) {
  const [edit, setEdit] = useState<RoomEdit>({
    name: room?.name ?? '',
    capacity: room?.capacity ?? null,
    color: room?.color ?? null,
    hasBarres: room?.hasBarres ?? false,
    hasMirrors: room?.hasMirrors ?? false,
    hasSprungFloor: room?.hasSprungFloor ?? false,
    notes: room?.notes ?? null,
  })

  const save = useMutation({
    mutationFn: () => (room ? saveRoom(room.roomId, edit) : createRoom(edit)),
    onSuccess: onSaved,
  })

  const set = (patch: Partial<RoomEdit>) => setEdit((e) => ({ ...e, ...patch }))

  return (
    <div className={room ? 'mt-3 flex flex-col gap-3' : 'mb-3 rounded-2xl border border-accent bg-surface px-4 py-3.5'}>
      <div className="flex flex-wrap gap-2.5">
        <label className="min-w-[10rem] flex-1">
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">Name</span>
          <input value={edit.name} onChange={(e) => set({ name: e.target.value })} className={field} />
        </label>

        <label className="w-24">
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">Fits</span>
          <input
            type="number"
            min={1}
            value={edit.capacity ?? ''}
            onChange={(e) => set({ capacity: e.target.value ? Number(e.target.value) : null })}
            className={field}
          />
        </label>

        <label className="w-28">
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">Colour</span>
          <input
            type="color"
            value={edit.color ?? '#cccccc'}
            onChange={(e) => set({ color: e.target.value })}
            className="h-[38px] w-full rounded-lg border border-line-2 bg-surface px-1"
          />
        </label>
      </div>

      <div className="flex flex-wrap gap-4">
        {([
          ['hasBarres', 'Barres'],
          ['hasMirrors', 'Mirrors'],
          ['hasSprungFloor', 'Sprung floor'],
        ] as const).map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={edit[key]}
              onChange={(e) => set({ [key]: e.target.checked } as Partial<RoomEdit>)}
              className="size-4 accent-[var(--color-accent)]"
            />
            {label}
          </label>
        ))}
      </div>

      <label>
        <span className="mb-1 block text-[12px] font-semibold text-ink-3">Notes</span>
        <input
          value={edit.notes ?? ''}
          onChange={(e) => set({ notes: e.target.value || null })}
          placeholder="Anything a teacher should know before booking it"
          className={field}
        />
      </label>

      {save.isError && (
        <p role="alert" className="text-[12.5px] text-crit">
          {(save.error as Error).message}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!edit.name.trim() || save.isPending}
          className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
        >
          {save.isPending ? 'Saving...' : room ? 'Save' : 'Add room'}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
        >
          Cancel
        </button>

        {footer}
      </div>
    </div>
  )
}

function Seasons() {
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)

  const { data, isPending } = useQuery({ queryKey: ['admin-seasons'], queryFn: fetchSeasons })
  const put = (seasons: Season[]) => queryClient.setQueryData(['admin-seasons'], seasons)

  return (
    <section>
      <header className="mb-3 flex items-center gap-2.5">
        <CalendarRange size={16} strokeWidth={2} className="shrink-0 text-ink-3" />
        <h2 className="flex-1 text-[13px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          Seasons
        </h2>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent-2"
          >
            <Plus size={13} strokeWidth={2.5} />
            Add a season
          </button>
        )}
      </header>

      {isPending && <p className="text-[13px] text-ink-3">Loading...</p>}

      {adding && (
        <SeasonForm
          onCancel={() => setAdding(false)}
          onSaved={(seasons) => {
            put(seasons)
            setAdding(false)
          }}
        />
      )}

      <div className="flex flex-col gap-2">
        {data?.map((season) => <SeasonRow key={season.seasonId} season={season} onSaved={put} />)}
      </div>

      {/*
        Said once, because it is the misconception this screen invites. Moving a
        season's dates does not move a single class — occurrences key off class
        dates, never these (D-42).
      */}
      <p className="mt-3 text-[12.5px] leading-relaxed text-ink-3">
        Changing a season&rsquo;s dates does not move any class. Classes carry their own start
        and end dates, and the calendar is drawn from those.
      </p>
    </section>
  )
}

/**
 * The dates the studio works towards (D-555).
 *
 * <b>Markers, not instructions.</b> Nothing here cancels a class or closes the
 * studio - that is a closure, which is a different thing with consequences,
 * and keeping them apart is what lets this list be edited freely.
 *
 * Past dates stay on the screen under their own heading. A mistyped year is
 * the likeliest mistake, and a row that silently stops appearing is how it
 * goes unnoticed.
 */
function KeyDates() {
  const queryClient = useQueryClient()
  const [adding, setAdding] = useState(false)

  const { data, isPending, isError } = useQuery({
    queryKey: ['studio-dates'],
    queryFn: fetchStudioDates,
  })
  const put = (dates: StudioDate[]) => queryClient.setQueryData(['studio-dates'], dates)

  const ahead = data?.filter((d) => !d.isPast) ?? []
  const gone = data?.filter((d) => d.isPast) ?? []

  return (
    <section className="mt-8">
      <header className="mb-3 flex items-center gap-2.5">
        <CalendarDays size={16} strokeWidth={2} className="shrink-0 text-ink-3" />
        <h2 className="flex-1 text-[13px] font-bold tracking-[0.11em] text-ink-3 uppercase">
          Key dates
        </h2>
        {!adding && (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex items-center gap-1.5 rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-accent hover:text-accent-2"
          >
            <Plus size={13} strokeWidth={2.5} />
            Add a date
          </button>
        )}
      </header>

      {isPending && <p className="text-[13px] text-ink-3">Loading...</p>}

      {adding && (
        <StudioDateForm
          onCancel={() => setAdding(false)}
          onSaved={(dates) => {
            put(dates)
            setAdding(false)
          }}
        />
      )}

      <div className="flex flex-col gap-2">
        {ahead.map((date) => (
          <StudioDateRow key={date.studioDateId} date={date} onSaved={put} />
        ))}
      </div>

      {gone.length > 0 && (
        <>
          <p className="mt-4 mb-2 text-[12px] font-semibold tracking-[0.08em] text-ink-3 uppercase">
            Gone by
          </p>
          <div className="flex flex-col gap-2">
            {gone.map((date) => (
              <StudioDateRow key={date.studioDateId} date={date} onSaved={put} />
            ))}
          </div>
        </>
      )}

      {/*
        A failed load is not an empty list, and this is the one section that
        would otherwise make a positive claim about her data (D-209). Every
        database-backed endpoint refuses for about 75 seconds after a deploy
        (D-33), so this is a normal state rather than a rare one.
      */}
      {isError && (
        <p role="alert" className="text-[13px] text-crit">
          The key dates did not load. This is usually a minute after an update &mdash; try
          again shortly.
        </p>
      )}

      {!isPending && !isError && (data?.length ?? 0) === 0 && (
        <p className="text-[13px] text-ink-3">
          Nothing yet. Competitions, the recital, picture day, a costume deadline &mdash;
          anything a job might need to be finished before.
        </p>
      )}

      {/*
        Said once, because it is the misconception a list of dates invites on a
        screen that also holds the timetable's own setup.
      */}
      <p className="mt-3 text-[12.5px] leading-relaxed text-ink-3">
        These are markers. They do not cancel classes or close the studio, and moving one
        does not move a job somebody has already been given.
      </p>
    </section>
  )
}

function StudioDateRow({
  date,
  onSaved,
}: {
  date: StudioDate
  onSaved: (d: StudioDate[]) => void
}) {
  const [open, setOpen] = useState(false)
  const retire = useMutation({ mutationFn: () => archiveStudioDate(date.studioDateId), onSuccess: onSaved })

  return (
    <div className="rounded-2xl border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold wrap-anywhere">
            {date.name}
            {date.isArchived && (
              <span className="ml-2 rounded-md bg-line px-1.5 py-0.5 text-[11px] font-semibold text-ink-3">
                Retired
              </span>
            )}
          </p>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            {formatFullDate(date.on)}
            {date.note && <> &middot; {date.note}</>}
          </p>
        </div>

        {!open && !date.isArchived && (
          <>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
            >
              Edit
            </button>
            {/*
              Never refused, unlike a room (D-557): anything hung on this date
              already copied its own deadline when it was raised, so retiring
              it breaks nothing and old rows still read its name.
            */}
            <button
              type="button"
              onClick={() => retire.mutate()}
              disabled={retire.isPending}
              className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-3 hover:border-crit hover:text-crit disabled:opacity-40"
            >
              Retire
            </button>
          </>
        )}
      </div>

      {retire.isError && (
        <p role="alert" className="mt-2 text-[12.5px] text-crit">
          {(retire.error as Error).message}
        </p>
      )}

      {open && (
        <StudioDateForm
          date={date}
          onCancel={() => setOpen(false)}
          onSaved={(dates) => {
            onSaved(dates)
            setOpen(false)
          }}
        />
      )}
    </div>
  )
}

function StudioDateForm({
  date,
  onCancel,
  onSaved,
}: {
  date?: StudioDate
  onCancel: () => void
  onSaved: (dates: StudioDate[]) => void
}) {
  const [edit, setEdit] = useState<StudioDateEdit>({
    name: date?.name ?? '',
    on: date?.on ?? '',
    note: date?.note ?? null,
  })

  const save = useMutation({
    mutationFn: () => (date ? saveStudioDate(date.studioDateId, edit) : createStudioDate(edit)),
    onSuccess: onSaved,
  })

  const set = (patch: Partial<StudioDateEdit>) => setEdit((e) => ({ ...e, ...patch }))

  /*
   * Held back until she has typed something, or opening the form announces
   * "Give it a name" to a screen reader before she has had the chance. The
   * button stays disabled either way, which is where the real refusal lives.
   */
  const problem = studioDateProblem(edit)
  const showProblem = problem !== null && (edit.name.trim() !== '' || edit.on !== '')

  return (
    <div
      className={
        date
          ? 'mt-3 flex flex-col gap-3'
          : 'mb-3 flex flex-col gap-3 rounded-2xl border border-accent bg-surface px-4 py-3.5'
      }
    >
      <div className="flex flex-wrap gap-2.5">
        <label className="min-w-[11rem] flex-1">
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">Name</span>
          <input
            value={edit.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Regionals"
            maxLength={120}
            className={field}
          />
        </label>

        <label className="min-w-[9rem] flex-1">
          <span className="mb-1 block text-[12px] font-semibold text-ink-3">Date</span>
          <input
            type="date"
            value={edit.on}
            onChange={(e) => set({ on: e.target.value })}
            className={field}
          />
        </label>
      </div>

      <label>
        <span className="mb-1 block text-[12px] font-semibold text-ink-3">
          Note <span className="font-normal">(optional)</span>
        </span>
        <input
          value={edit.note ?? ''}
          onChange={(e) => set({ note: e.target.value || null })}
          placeholder="Hot Springs, three days"
          maxLength={500}
          className={field}
        />
      </label>

      {(showProblem || save.isError) && (
        <p role="alert" className="text-[12.5px] text-crit">
          {problem ?? (save.error as Error).message}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={problem !== null || save.isPending}
          className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
        >
          {save.isPending ? 'Saving...' : date ? 'Save' : 'Add date'}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function SeasonRow({ season, onSaved }: { season: Season; onSaved: (s: Season[]) => void }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="rounded-2xl border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold wrap-anywhere">
            {season.name}
            {season.isCurrent && (
              <span className="ml-2 rounded-md bg-good-bg px-1.5 py-0.5 text-[11px] font-semibold text-good">
                Current
              </span>
            )}
          </p>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            {formatFullDate(season.startDate)} &ndash; {formatFullDate(season.endDate)}
            {season.classes > 0 && (
              <>
                {' · '}
                <span className="tabular">
                  {season.classes} {season.classes === 1 ? 'class' : 'classes'}
                </span>
              </>
            )}
          </p>
        </div>

        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
          >
            Edit
          </button>
        )}
      </div>

      {open && (
        <SeasonForm
          season={season}
          onCancel={() => setOpen(false)}
          onSaved={(seasons) => {
            onSaved(seasons)
            setOpen(false)
          }}
        />
      )}
    </div>
  )
}

function SeasonForm({
  season,
  onCancel,
  onSaved,
}: {
  season?: Season
  onCancel: () => void
  onSaved: (seasons: Season[]) => void
}) {
  const [edit, setEdit] = useState<SeasonEdit>({
    name: season?.name ?? '',
    startDate: season?.startDate ?? '',
    endDate: season?.endDate ?? '',
    registrationOpensOn: season?.registrationOpensOn ?? null,
    registrationClosesOn: season?.registrationClosesOn ?? null,
    ageCutoffDate: season?.ageCutoffDate ?? null,
  })

  const save = useMutation({
    mutationFn: () => (season ? saveSeason(season.seasonId, edit) : createSeason(edit)),
    onSuccess: onSaved,
  })

  const set = (patch: Partial<SeasonEdit>) => setEdit((e) => ({ ...e, ...patch }))

  // Checked here as well as on the server, so the answer arrives while somebody
  // is still looking at the field they got wrong.
  const problem = seasonDateProblem(edit)

  const dateField = (
    label: string,
    key: keyof SeasonEdit,
    hint?: string,
  ) => (
    <label className="min-w-[9rem] flex-1">
      <span className="mb-1 block text-[12px] font-semibold text-ink-3">{label}</span>
      <input
        type="date"
        value={(edit[key] as string | null) ?? ''}
        onChange={(e) => set({ [key]: e.target.value || null } as Partial<SeasonEdit>)}
        className={field}
      />
      {hint && <span className="mt-1 block text-[11.5px] text-ink-3">{hint}</span>}
    </label>
  )

  return (
    <div className={season ? 'mt-3 flex flex-col gap-3' : 'mb-3 flex flex-col gap-3 rounded-2xl border border-accent bg-surface px-4 py-3.5'}>
      <label>
        <span className="mb-1 block text-[12px] font-semibold text-ink-3">Name</span>
        <input
          value={edit.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="2027-28"
          className={field}
        />
      </label>

      <div className="flex flex-wrap gap-2.5">
        {dateField('Season starts', 'startDate', 'Not the first class night')}
        {dateField('Season ends', 'endDate')}
      </div>

      <div className="flex flex-wrap gap-2.5">
        {dateField('Registration opens', 'registrationOpensOn')}
        {dateField('Registration closes', 'registrationClosesOn')}
        {dateField('Age as of', 'ageCutoffDate', 'The date ages are judged on')}
      </div>

      {(problem || save.isError) && (
        <p role="alert" className="text-[12.5px] text-crit">
          {problem ?? (save.error as Error).message}
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!edit.name.trim() || problem !== null || save.isPending}
          className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
        >
          {save.isPending ? 'Saving...' : season ? 'Save' : 'Add season'}
        </button>

        <button
          type="button"
          onClick={onCancel}
          className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
